import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription, combineLatest, zip, map, filter, tap, mergeMap, switchMap } from 'rxjs';
import { DateTime } from 'luxon';

import { Employee } from '@shared/models/employee';
import { SharedSettings } from '@shared/models/shared-settings';
import { PieceRateActivity } from '@shared/models/piece-rate-activity';
import { url } from '@shared/helpers/url';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { EmployeeService } from "@shared/services/employee.service";

import { PieceRateAdjSummary } from '@shared/models/piece-rate-summary';
import { PieceRateAdjustment, PieceRateAdjustmentEntryBuilder } from '@shared/models/piece-rate-adjustment';
import { CombinedShiftTotals } from '@shared/models/combined-shift-totals';
import { WeeklyTotals } from '@shared/models/weekly-totals';
import { PieceRateSiteWeek } from '@shared/models/piece-rate-site-week';
import { ShiftContextService } from './shift-context.service';
import { PieceRateRequestService } from "@shared/services/piece-rate-request.service";

@Injectable({
    providedIn: 'root'
})
export class PieceRateService {

    // current "context" facts
    private readonly _currentBuilders       = new BehaviorSubject<Employee[]>(null);
    private readonly _currentAdjustment     = new BehaviorSubject<PieceRateAdjustment | null>(null);
    private readonly _currentActivity       = new BehaviorSubject<number>(1);
    private readonly _pieceRateSummary      = new BehaviorSubject<PieceRateAdjSummary>(null);
    private readonly _currentSiteWeek       = new BehaviorSubject<PieceRateSiteWeek>(null);
    private readonly _currentCombinedTotals = new BehaviorSubject<CombinedShiftTotals>(null);

    get CurrentActivity(): Observable<number> {
        return this._currentActivity;
    }

    get CurrentCombinedTotals(): Observable<CombinedShiftTotals> {
        return this._currentCombinedTotals;
    }

    get CurrentBuilders(): Observable<Employee[]> {
        return this._currentBuilders.asObservable().pipe(filter(builders => builders !== null));
    }

    get CurrentPieceRateAdjustment(): Observable<PieceRateAdjustment | null> {
        return this._currentAdjustment.asObservable();
    }

    get CurrentPieceRateSummary(): Observable<PieceRateAdjSummary> {
        return this._pieceRateSummary.asObservable().pipe(filter(summary => !!summary));
    }

    get CurrentSiteWeek(): Observable<PieceRateSiteWeek> {
        return this._currentSiteWeek.asObservable();
    }

    // calculated things

    get CurrentEnteredTotal(): Observable<number> {
        return combineLatest([this._currentAdjustment, this._currentActivity]).pipe(map(([adjustment, activityId]) => {
            const entries: PieceRateAdjustmentEntryBuilder[] = adjustment?.builderEntries
                .filter(a => a.pieceRateActivityId === activityId);
            const config = adjustment?.activityConfigs.find(ac => ac.pieceRateActivity === activityId);

            return entries
                ?.reduce((agg, be) => agg + (+be.count) + (config.showSecondaryCount ? +be.secondaryCount ?? 0 : 0), 0) ?? 0;
        }));
    }

    get NumBuilders(): Observable<number> {
        return combineLatest([this._currentAdjustment, this._currentActivity]).pipe(map(([adjustment, activityId]) => {
            const entries: PieceRateAdjustmentEntryBuilder[] = adjustment?.builderEntries
                .filter(a => a.pieceRateActivityId === activityId);

            return entries
                .filter(be => (+be.count) > 0 || (+be.secondaryCount || 0) > 0)
                .length ?? 0;
        }));
    }

    get CurrentAverage(): Observable<number> {
        return combineLatest([
            this.CurrentEnteredTotal, this.NumBuilders, this._pieceRateSummary, this._currentCombinedTotals, this._currentSiteWeek
        ]).pipe(map(([total, builders, summary, combinedTotals, siteWeek]) => {
            let invPieces = 0;
            let repairsByTemps = 0;
            let totBuilders = 0;
            if (summary) {
                invPieces = summary.adjustmentTotal;
                repairsByTemps = summary.adjustmentTotalByTemps;
                totBuilders = builders;
            }

            if (siteWeek?.combinedShifts && combinedTotals) {
                invPieces += combinedTotals.inventoryTotal;
                repairsByTemps += combinedTotals.byTemps;
                total += combinedTotals.builderTotal;
                totBuilders += combinedTotals.builderCount;
            }

            return totBuilders ? Math.trunc((invPieces - repairsByTemps - total) / totBuilders) : 0;
        }));
    }

    private subscriptions: Subscription[];
    private currentContext: { siteId: string, date: DateTime, shiftId: number, shiftNum: number };

    constructor(private activityService: ActivityBlockingService,
            private shiftContextService: ShiftContextService,
            private employeeService: EmployeeService,
            private pieceRateReqeustService: PieceRateRequestService) {
    }

    load(): Observable<{ adjustmentExists: boolean, context: { siteId: string, date: DateTime, shiftId: number, shiftNum: number }}> {
        return combineLatest([this.shiftContextService.ImmediateSelection, this._currentActivity]).pipe(
            mergeMap(([context, activityId]) => {
                this.currentContext = context;
                const weekEndDate = this.getWeekEnding(context.date);

                return zip([
                    this.getPieceRateAdjustment(context.siteId, context.date, context.shiftId),
                    of(context),
                    this.getPieceRateSummary(context.siteId, context.date, context.shiftId, activityId),
                    this.GetSiteWeek(context.siteId, weekEndDate).pipe(switchMap(siteWeek => {
                        return this.getCombinedShiftTotals(context.siteId, context.date, context.shiftId, activityId);
                    })),
                    this.getBuilders(context.siteId, context.date)
                ]);
            }),
            map(([adjustmentExists, context, _summary, _week, _builders]) => ({adjustmentExists, context})),
            filter(r => r !== null));
    }



    unload(): void {
        this._currentBuilders.next(null);
        this._currentAdjustment.next(null);
        this._pieceRateSummary.next(null);
        this.subscriptions?.forEach(s => s.unsubscribe());
    }
    loadPalletSolutions(siteId: string, date: DateTime, shiftId: number): Observable<boolean> {
        const pieceRateLoadedObs = this.getPieceRateAdjustment(siteId, date, shiftId).pipe(map(x => {
            return x
        }))
        this.subscriptions = [
            pieceRateLoadedObs.subscribe(),
            this.getBuilders(siteId, date).subscribe()
        ];
        return pieceRateLoadedObs;
    }
    updateActiveSection(activity: number): void {
        if (this._currentActivity.value !== activity)
            this._currentActivity.next(activity);
    }

    getBuilders(siteId: string, date: DateTime): Observable<Employee[]> {
        return this.employeeService.getEmployeesBySiteDate(siteId, date)
            .pipe(
                this.activityService.Wait('Getting Builders'),
                tap(currentBuilders => {
                    currentBuilders.sort((a, b) => a.firstName.localeCompare(b.firstName));
                    this._currentBuilders.next(currentBuilders);
                }));
    }

    private GetSiteWeek(siteId: string, weekEndDate: DateTime): Observable<PieceRateSiteWeek> {
        return this.pieceRateReqeustService.getPieceRateSiteWeek(siteId, weekEndDate)
            .pipe(tap(siteWeek => {
                this._currentSiteWeek.next(siteWeek);
            }));
    }

    private getPieceRateAdjustment(siteId: string, date: DateTime, shiftId: number): Observable<boolean> {
        return this.pieceRateReqeustService.getPieceRateAdjustment(siteId, date, shiftId)
            .pipe(
                this.activityService.Wait(`Getting Piece Rate Adjustment ${siteId}/${date.toISODate()}/${shiftId}`),
                map(adj => {
                    this._currentAdjustment.next(adj);

                    return !!adj;
                }));
    }

    private getPieceRateSummary(siteId: string, date: DateTime, shiftId: number, activityId: number): Observable<boolean> {
        return !activityId
            ? of(null)
            : this.pieceRateReqeustService.getPieceRateSummary(siteId, date, shiftId, activityId)
                .pipe(
                    this.activityService.Wait(`Getting Piece Rate Summary ${siteId}/${date.toISODate()}/${shiftId}`),
                    map(summary => {
                        if (summary) {
                            summary.date = DateTime.fromISO(<any>summary.date);
                            this._pieceRateSummary.next(summary);
                        }

                        return !!summary;
                    }));
    }

    private getCombinedShiftTotals(
        siteId: string, date: DateTime, shiftId: number,
        activityId: number): Observable<CombinedShiftTotals> {

        return this.pieceRateReqeustService.getCombinedShiftTotals(siteId, date, shiftId, activityId)
            .pipe(
                this.activityService.Wait(`Getting Combined Shift Totals for ${siteId}/${date.toISODate()}/${shiftId}`),
                map(totals => {
                    if (totals)
                        this._currentCombinedTotals.next(totals);

                    return totals;
                }));
    }

    createPieceRateAdjustment(): void {
        this.pieceRateReqeustService.createPieceRateAdjustment(this.currentContext.siteId, this.currentContext.date, this.currentContext.shiftId)
            .pipe(this.activityService.Wait('Creating new Piece Rate Adjustment'))
            .subscribe({
                next: adj => {
                    this._currentAdjustment.next(adj);
                },
                error: _error => {
                    this._currentAdjustment.next(null);
                }
            });
    }

    createPieceRateSummary(): void {
        this.pieceRateReqeustService.createPieceRateSummary(this.currentContext.siteId, this.currentContext.date, this.currentContext.shiftId)
            .pipe(this.activityService.Wait('Creating Piece Rate Summary'))
            .subscribe(summary => {
                summary.date = DateTime.fromISO(<any> summary.date);

                this._pieceRateSummary.next(summary);
            });
    }

    putPieceRateSummary(summary: PieceRateAdjSummary): void {
        this.pieceRateReqeustService.putPieceRateSummary(summary)
            .pipe(this.activityService.Wait('Updating Piece Rate Summary'))
            .subscribe(summaryObj => {
                summaryObj.date = DateTime.fromISO(<any> summaryObj.date);
                this._pieceRateSummary.next(summaryObj);
            });
    }

    postBuilderEntry(adjustmentId: number, entry: PieceRateAdjustmentEntryBuilder):
            Observable<PieceRateAdjustmentEntryBuilder> {
        return !(adjustmentId && entry)
            ? of(null)
            : this.pieceRateReqeustService.postBuilderEntry(adjustmentId, entry)
                .pipe(tap(newEntry => {
                    const adj = this._currentAdjustment.value;
                    adj.builderEntries.push(newEntry);
                    this._currentAdjustment.next(adj);
                }));
    }

    updateBuilderEntry(adjustmentId: number, builderEntryId: number, entry: PieceRateAdjustmentEntryBuilder):
            Observable<PieceRateAdjustmentEntryBuilder> {
        return !(adjustmentId && builderEntryId && entry)
            ? of(null)
            : this.pieceRateReqeustService.updateBuilderEntry(adjustmentId, builderEntryId, entry)
                .pipe(tap(updatedEntry => {
                    const adj = this._currentAdjustment.value;
                    const oldEntry = adj.builderEntries.find(x => x.id === updatedEntry.id);
                    adj.builderEntries.splice(adj.builderEntries.indexOf(oldEntry),
                    1,
                    updatedEntry);
                    this._currentAdjustment.next(adj);
                }));
    }

    deleteBuilderEntry(adjustmentId: number, builderEntryId: number): Observable<any> {
        return !(adjustmentId && builderEntryId)
            ? of(null)
            : this.pieceRateReqeustService.deleteBuilderEntry(adjustmentId, builderEntryId)
                .pipe(tap(_ => {
                    const adj = this._currentAdjustment.value;
                    const deletedEntry = adj.builderEntries.find(x => x.id === builderEntryId);
                    adj.builderEntries.splice(adj.builderEntries.indexOf(deletedEntry), 1);
                    this._currentAdjustment.next(adj);
                }));
    }

    GetWeekStatus(siteId: string, weekEnding: DateTime): Observable<string[]> {
        return this.pieceRateReqeustService.getPieceRateWeekStatus(siteId, weekEnding);
    }

    postSiteWeekClosed(siteId: string, weekEnding: DateTime, closed: boolean): Observable<any> {
        if (!(siteId && weekEnding))
            return of(null);

        return this.pieceRateReqeustService.postSiteWeekClosed(siteId, weekEnding, closed).
        pipe(tap(_ => {
            const adj = this._currentAdjustment.value;
            adj.weekIsClosed = !adj.weekIsClosed;
            this._currentAdjustment.next(adj);
        }));
    }

    postSiteWeekCombinedShifts(siteId: string, weekEnding: DateTime, combinedShifts: boolean): Observable<any> {
        return !(siteId && weekEnding)
            ? of(null)
            : this.pieceRateReqeustService.postSiteWeekCombinedShifts(siteId, weekEnding, combinedShifts)
                .pipe(tap(_ => {
                    const wk = this._currentSiteWeek.value;
                    wk.combinedShifts = combinedShifts;
                    this._currentSiteWeek.next(wk);
                }));
    }

    pieceRateReset(adjustmentId: number, pieceRateActivityId: number): Observable<any> {
        return !(adjustmentId && pieceRateActivityId)
            ? of(null)
            : this.pieceRateReqeustService.pieceRateReset(adjustmentId, pieceRateActivityId)
                .pipe(switchMap(_ => zip([
                    this.getPieceRateAdjustment(this.currentContext.siteId, this.currentContext.date, this.currentContext.shiftId),
                    this.getPieceRateSummary(
                        this.currentContext.siteId,
                        this.currentContext.date,
                        this.currentContext.shiftId,
                        pieceRateActivityId)
                ])));
    }

    CheckWeeklyTotals(weekEnding: DateTime, pieceRateActivity: PieceRateActivity): Observable<WeeklyTotals[]> {
        return this.pieceRateReqeustService.checkWeeklyTotals(weekEnding, pieceRateActivity, this.currentContext.siteId);
    }
    getWeekEnding(currentDate: DateTime): DateTime {
        switch (currentDate.weekday) {
            case 7: // sunday
                return currentDate.plus({ days: 6 });
            default:
                return currentDate.plus({ days: 6 - currentDate.weekday });
        }
    }
}
